import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ExternalLink, Download } from "lucide-react";
import customerCareIcon from "@/assets/customer-care-icon.png";
import lelyHeroBg from "@/assets/lely-hero-bg.jpg";
import lelyLogo from "@/assets/lely-logo.png";
import lelyAstronautA5 from "@/assets/lely-astronaut-a5-new.jpg";
import lelyVector from "@/assets/lely-vector-new.jpg";
import lelyCalm from "@/assets/lely-calm-new.jpg";
import lelyMqcC from "@/assets/lely-mqc-c-new.jpg";
import lelyDiscovery from "@/assets/lely-discovery-new.jpg";
import lelyJuno from "@/assets/lely-juno-new.jpg";
import lelyLuna from "@/assets/lely-luna-new.jpg";

const ProductLely = () => {
  return (
    <>
      <Helmet>
        <title>LELY - Avant-gardiste dans l'automatisation des fermes laitières</title>
        <meta 
          name="description" 
          content="Leader dans l'Est-du-Québec pour les solutions robotisées en agriculture. Découvrez nos produits LELY." 
        />
        <link rel="preload" as="image" href={lelyHeroBg} />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero Section */}
        <section 
          className="relative bg-cover bg-center text-white py-32"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${lelyHeroBg})`,
            minHeight: '500px'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl bg-black/65 backdrop-blur-sm rounded-lg p-8 md:p-10">
              <div className="w-16 h-1 bg-accent mb-6"></div>
              <h1 className="text-3xl md:text-5xl font-bold mb-6">
                LELY: Avant-gardiste dans l'automatisation des fermes laitières
              </h1>
              <p className="text-lg leading-relaxed mb-4">
                En tant que leader dans l'Est-du-Québec pour les solutions robotisées en 
                agriculture, Agro est fier de s'associer à LELY depuis 2008. Référence mondiale 
                dans l'automatisation des fermes laitières, cette marque néerlandaise se distingue 
                par son esprit avant-gardiste, son engagement envers la technologie de pointe et le 
                respect de l'environnement.
              </p>
              <p className="text-lg leading-relaxed">
                Nos produits LELY ont été conçus pour répondre aux défis de l'agriculture 
                d'aujourd'hui et de demain. Ils offrent une gamme de solutions, que vous cherchiez 
                à optimiser la traite, l'alimentation ou l'entretien de votre ferme laitière. 
                Explorez notre sélection et découvrez pourquoi LELY est le choix privilégié des 
                agriculteurs de l'Est-du-Québec.
              </p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="bg-white py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  Les produits LELY: une technologie avancée pour une agriculture efficace
                </h2>
                <p className="text-muted-foreground mb-2">
                  Découvrez-en plus sur{" "}
                  <a 
                    href="https://www.lely.com/ca/fr/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-accent hover:underline font-medium"
                  >
                    Lely.com
                  </a>
                </p>
              </div>
              <div>
                <p className="text-muted-foreground leading-relaxed">
                  Agro propose une gamme diversifiée de produits LELY, chacun conçu pour optimiser 
                  un aspect spécifique de votre exploitation agricole. De la traite à l'alimentation, 
                  découvrez comment ces solutions peuvent transformer votre ferme.
                </p>
              </div>
            </div>

            {/* Brochure 1 - La traite robotisée Lely */}
            <div className="grid md:grid-cols-2 items-stretch mb-16 bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="flex items-center justify-center bg-gray-50 p-16">
                <img 
                  src={lelyLogo} 
                  alt="Logo LELY"
                  className="w-full max-w-md h-auto"
                />
              </div>
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  La traite robotisée Lely: Maximiser la rentabilité, optimiser la gestion
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Libérez le potentiel de votre ferme avec la traite robotisée Lely. Ce système 
                  innovant offre un contrôle sans précédent, optimise la rentabilité et révolutionne 
                  votre gestion quotidienne. Avec des coûts fixes et prévisibles, il facilite le 
                  contrôle de votre budget, tout en minimisant la dépendance à la main-d'œuvre. 
                  Concentrez-vous sur ce qui compte vraiment pour votre élevage et découvrez un 
                  nouveau niveau de productivité.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-traite-robotisee.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Brochure 2 - Astronaut A5 */}
            <div className="grid md:grid-cols-2 items-stretch mb-16 bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  Lely Astronaut A5: Une innovation pour la traite automatisée
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Le Lely Astronaut A5 est plus qu'un simple robot de traite. Il est l'incarnation 
                  de l'innovation technologique et du confort animal, créant un environnement calme 
                  et optimisé pour une production laitière accrue. En surveillant individuellement 
                  chaque vache, il fournit des informations précieuses sur la santé, l'alimentation 
                  et la production laitière.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-astronaut-a5.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-center bg-gray-50">
                <img 
                  src={lelyAstronautA5} 
                  alt="Lely Astronaut A5"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Brochure 3 - Lely Vector */}
            <div className="grid md:grid-cols-2 items-stretch mb-16 bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="flex items-center justify-center bg-gray-50">
                <img 
                  src={lelyVector} 
                  alt="Lely Vector"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  Lely Vector: Une alimentation automatique adaptée à vos besoins
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Optimisez la santé, la fertilité et la production de votre ferme avec Lely Vector, 
                  notre solution d'alimentation automatique. En proposant des rations sur mesure 
                  adaptées à chaque stade de vie de vos animaux, Lely Vector favorise une nutrition 
                  optimale pour un meilleur rendement.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-vector.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Brochure 4 - Lely Calm */}
            <div className="grid md:grid-cols-2 items-stretch mb-16 bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  Lely Calm: Croissance et développement optimaux de veau à vache laitière
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Avec le système d'alimentation des veaux Lely, réduisez la période de reproduction 
                  pour augmenter la production à vie. Offrez à chaque veau des portions de lait sur 
                  mesure, adaptées à son âge et à ses besoins grâce à l'accès illimité à notre système.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-calm.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-center bg-gray-50">
                <img 
                  src={lelyCalm} 
                  alt="Lely Calm"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Brochure 5 - MQC-C */}
            <div className="grid md:grid-cols-2 items-stretch mb-16 bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="flex items-center justify-center bg-gray-50">
                <img 
                  src={lelyMqcC} 
                  alt="MQC-C de Lely"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  MQC-C de Lely: Le contrôle intelligent de la santé des mamelles
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  La santé des mamelles est cruciale pour la qualité du lait et la santé globale de 
                  vos vaches. Grâce au MQC-C de Lely, vous pouvez prendre le contrôle et agir de 
                  manière proactive contre les maladies telles que la mammite.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-mqc-c.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Brochure 6 - Discovery */}
            <div className="grid md:grid-cols-2 items-stretch mb-16 bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  Nettoyage des sols d'étable Lely: Une hygiène inégalée
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  L'hygiène des étables est un facteur clé dans la promotion de la santé et de la 
                  productivité du bétail. Lely offre une solution innovante: les nettoyeurs d'étable 
                  Discovery, assurant un environnement impeccable, jour et nuit.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-discovery.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-center bg-gray-50">
                <img 
                  src={lelyDiscovery} 
                  alt="Nettoyage des sols d'étable Lely"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Brochure 7 - Lely Juno */}
            <div className="grid md:grid-cols-2 items-stretch mb-16 bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="flex items-center justify-center bg-gray-50">
                <img 
                  src={lelyJuno} 
                  alt="Robot Repousse Fourrage Lely Juno"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  Robot Repousse Fourrage Lely Juno
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Transformez votre alimentation du bétail avec le Lely Juno. Ce robot repousse 
                  fourrage favorise un comportement alimentaire sain pour votre troupeau, augmentant 
                  ainsi la qualité du lait.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-juno.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Brochure 8 - Lely Luna */}
            <div className="grid md:grid-cols-2 items-stretch bg-white rounded-lg overflow-hidden shadow-sm min-h-[400px]">
              <div className="bg-gray-100 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">
                  Brosse pour Vaches Lely Luna
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  La brosse pour vaches Luna de Lely est la solution pour un troupeau heureux et en 
                  santé. Grâce à son design ingénieux, cette brosse procure un confort optimal et 
                  stimule la circulation sanguine.
                </p>
                <div>
                  <Button asChild className="bg-accent hover:bg-accent/90 text-white gap-2 px-6 py-3">
                    <a href="/brochures/lely-luna.pdf" download>
                      <Download className="w-5 h-5" />
                      Télécharger la brochure
                    </a>
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-center bg-gray-50">
                <img 
                  src={lelyLuna} 
                  alt="Brosse pour Vaches Lely Luna"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative bg-[#333333] text-white py-16 overflow-hidden">
          <img src={customerCareIcon} alt="" className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 object-contain pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <p className="text-sm uppercase tracking-wider mb-4">CONTACTEZ-NOUS</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Optimiser Votre Production Laitière?
            </h2>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              Exploitez pleinement le potentiel de votre production laitière avec Agro. Contactez-nous dès aujourd&apos;hui pour découvrir comment
            </p>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              nos services et produits peuvent aider à améliorer l&apos;efficacité de votre exploitation et garantir la meilleure qualité de lait possible.
            </p>
            <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
              Nous sommes disponibles 24/7 pour vous assister dans vos urgences.
            </p>
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
              <Link to="/contact">Contactez-nous dès maintenant!</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductLely;
